import type { Project } from "shared/Project";
import { raceLoaderData } from "./LoaderResult";

/** What the organisation and project layout routes hand their pages (see their loaders). */
export interface PortalScopeData {
  orgId: string;
  /** The project the URL names, or for an organisation page the visitor's last one in it. */
  projectId: string | null;
  project: Project | null;
}

interface ScopedLoaderContext {
  parentMatchPromise: Promise<{ loaderData?: unknown }>;
}

/** The scope the organisation and project layout routes resolved, from a page's loader. */
export const scopeFrom = async (context: ScopedLoaderContext) =>
  (await context.parentMatchPromise).loaderData as PortalScopeData;

/**
 * A project page's loader: `load` gets the project the URL names (resolved by the layout routes),
 * raced like every page's data (`raceLoaderData`).
 */
export const projectLoader =
  <T, C extends ScopedLoaderContext = ScopedLoaderContext>(
    load: (projectId: string, context: C) => Promise<T>,
  ) =>
  (context: C) =>
    raceLoaderData(
      scopeFrom(context).then((scope) => {
        if (!scope.projectId) throw new Error("A project page rendered without its project");
        return load(scope.projectId, context);
      }),
    );

/** An organisation page's loader: `load` gets the organisation the URL names. */
export const organisationLoader =
  <T, C extends ScopedLoaderContext = ScopedLoaderContext>(
    load: (orgId: string, context: C) => Promise<T>,
  ) =>
  (context: C) =>
    raceLoaderData(scopeFrom(context).then((scope) => load(scope.orgId, context)));

/**
 * The scope key of the portal layout data on screen (browser), for the layout's `shouldReload`.
 * It lives here rather than in the route module: route code splitting gives the loader and the
 * component their own copies of a route module's variables.
 */
let loadedPortalScopeKey: string | undefined;
/** A loaded scope key no page has, so the portal layout's data loads again for the next page. */
export const STALE_PORTAL_SCOPE = "stale";
export const getLoadedPortalScopeKey = () => loadedPortalScopeKey;
export const setLoadedPortalScopeKey = (key: string) => {
  loadedPortalScopeKey = key;
};
